import { useEffect, useState, KeyboardEvent, useRef } from "react";
import { observer } from "mobx-react";
import { Modal, Form, Input, Radio, InputRef } from "antd";
import { Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import SJButton from "@/components/SJButton";
import cls from "classnames";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UserSmscodeIcon } from "@/assets/images/user-smscode.svg";
import { ReactComponent as UserNameIcon } from "@/assets/images/user-name.svg";
import { REQUEST_ERR_TOAST } from "logic/src/constants/request";
import scrollBlock from "@/assets/images/scroll-block2.png";
import i18next from "i18next";
import { AgreeType, ELoginType } from "logic/src/objects/user/types";
import { PHONE_PATTERNS, REGIONS } from "@/store/const";
import PolicyDialog from "@/components/PolicyDialog";
import CountDownButton from "logic/src/components/CountDownButton";
import PasswordEye from "@/components/PasswordEye";
import useTrackCLK from "logic/src/utils/useTrackCLK";
import { validatePassword } from "logic/src/utils";
import { ResCode } from "logic/src/constants/request";
import { SenseChat } from "logic/src/utils/error";
import useMessage from "logic/src/utils/useMessage";
import { ReactComponent as UserPasswordIcon } from "@/assets/images/user-password.svg";
import { AUTH_ERROR_TIP, LOGIN_TYPE } from "logic/src/constants/user";
import authVM from "logic/src/businesses/auth/vm";
import { sendCLK } from "logic/src/utils/eventTrack";

import styles from "../../index.module.scss";

const INPUT_ICON_STYLE = {
  color: "#8F9AAF",
};

const LoginDialog: React.FC<{
  onFinishLogin: () => void;
  onGotoResetPassword: () => void;
}> = ({ onFinishLogin, onGotoResetPassword }) => {
  const [form] = Form.useForm();
  const [showProtocol, setShowProtocol] = useState(false);

  const mobile = Form.useWatch("mobile", form);
  const sms_code = Form.useWatch("sms_code", form);
  const password = Form.useWatch("password", form);
  const agreement = Form.useWatch("agreement", form);

  const phoneInputRef = useRef<InputRef>(null);

  useEffect(() => {
    phoneInputRef.current?.focus();
  }, []);

  const showToast = (text: string) => {
    message.warning(text);
  };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useMessage();

  // 弹窗协议勾选
  const handleAgreeProtocol = async () => {
    try {
      form.setFieldValue("agreement", true);
      setShowProtocol(false);
      if (authVM.loginType === ELoginType.sms_code) {
        await authVM.loginByMobil({
          mobile,
          sms_code,
        });
      } else {
        await authVM.loginByMobil({
          mobile,
          password,
        });
      }
      sendCLK("login_success", "登录成功");
      onFinishLogin();
    } catch (error: any) {
      sendCLK("login_failure", "登陆失败");
      if (error && error.code) {
        if (
          error.code === SenseChat.Status.UserDisableError &&
          error.message?.unbannedtime
        ) {
          showToast(
            `您的账号可以在${dayjs(error.message?.unbannedtime).format(
              "YYYY年MM月DD日HH:mm",
            )}后登录`,
          );
        } else {
          showToast(AUTH_ERROR_TIP[error.code] || "登录失败");
        }
      } else {
        throw error;
      }
    }
  };

  const handleGetVerifyCode = async () => {
    try {
      sendCLK("login_code_click");
      const checkSuccess = await form
        .validateFields(["mobile"])
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      await authVM.getVerifyCode(mobile);
    } catch (error: any) {
      if (error && error.code) {
        showToast(AUTH_ERROR_TIP[error.code] || "发送失败");
      } else {
        throw error;
      }
    }
  };

  const handleLogin = async () => {
    try {
      sendCLK("login_login_click");
      const fields =
        (await authVM.loginType) === ELoginType.sms_code
          ? ["mobile", "sms_code"]
          : ["mobile", "password"];
      const checkSuccess = await form
        .validateFields(fields)
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      if (!agreement) {
        return setShowProtocol(true);
      }
      await authVM.loginByMobil({
        mobile,
        password,
        sms_code,
      });
      sendCLK("login_success", "登录成功");
      onFinishLogin();
    } catch (error: any) {
      sendCLK("login_failure", "登陆失败");
      if (error && error.code) {
        if (
          error.code === SenseChat.Status.UserDisableError &&
          error.message?.unbannedtime
        ) {
          showToast(
            `您的账号可以在${dayjs(error.message?.unbannedtime).format(
              "YYYY年MM月DD日HH:mm",
            )}后登录`,
          );
        } else {
          showToast(AUTH_ERROR_TIP[error.code] || "登录失败");
        }
      } else {
        throw error;
      }
    }
  };

  const handleShowAgreement = (
    e: React.MouseEvent<HTMLElement>,
    agreeType: AgreeType,
  ) => {
    e.preventDefault();
    window.open(`/policy?id=${agreeType}&lang=${i18next.language}`, "_blank");
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <>
      <Radio.Group
        className={styles["new-login-form-swap"]}
        value={authVM.loginType}
        onChange={(e: any) => authVM.setLoginType(e.target.value)}
      >
        {LOGIN_TYPE.map(item => (
          <Radio.Button
            className={cls(styles["new-login-form-swap-btn"], {
              "swap-btn-active": authVM.loginType === item.value,
            })}
            key={item.value}
            value={item.value}
          >
            {item.name}
          </Radio.Button>
        ))}
        <img
          style={{
            left: authVM.loginType === LOGIN_TYPE[0].value ? "32px" : "133px",
          }}
          className={styles["new-login-form-swap-scroller"]}
          src={scrollBlock}
          alt=""
        />
      </Radio.Group>
      <Form
        form={form}
        layout="horizontal"
        name="normal_login"
        className={styles["new-login-form"]}
        // onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="mobile"
          className={styles["new-login-form-cell"]}
          getValueFromEvent={event => {
            return event.target.value.replace(/\s+/g, "");
          }}
          validateTrigger="onConfirm"
          rules={[
            {
              required: true,
              message: t("sensechat-81") as string, // "请输入手机号"
            },
            {
              pattern: PHONE_PATTERNS[REGIONS.mainland],
              whitespace: true,
              message: t("sensechat-73") as string, // "请输入正确的手机号码"
            },
          ]}
        >
          <Input
            placeholder={t("sensechat-63") as string} // "请输入手机号"
            maxLength={11}
            className="large-input"
            prefix={<UserNameIcon style={INPUT_ICON_STYLE} />}
            autoComplete="off"
            autoFocus
            onKeyDown={handleKeyDown}
            ref={phoneInputRef}
            type="number"
          />
        </Form.Item>
        {authVM.loginType === ELoginType.sms_code ? (
          <Form.Item
            className={styles["new-login-form-cell"]}
            name="sms_code"
            validateTrigger="onConfirm"
            getValueFromEvent={event => {
              return event.target.value.replace(/\s+/g, "");
            }}
            rules={[
              {
                required: true,
                message: t("sensechat-74") as string, // "请输入短信验证码"
              },
              {
                max: 6,
                min: 6,
                whitespace: true,
                message: t("sensechat-100") as string, // "请输入合法的短信验证码"
              },
            ]}
          >
            <Input
              placeholder={t("sensechat-64") as string} // "短信验证码"
              maxLength={6}
              allowClear
              type="number"
              onKeyDown={handleKeyDown}
              className="large-input"
              prefix={<UserSmscodeIcon style={INPUT_ICON_STYLE} />}
              autoComplete="off"
              suffix={
                <CountDownButton
                  className="verify-code"
                  onSendCode={handleGetVerifyCode}
                  waiting={authVM.smsCode.waiting}
                  count={authVM.smsCode.count}
                >
                  {/* 发送验证码 */}
                  {t("sensechat-11")}
                </CountDownButton>
              }
            />
          </Form.Item>
        ) : (
          <Form.Item
            className={styles["new-login-form-cell"]}
            name="password"
            validateFirst
            validateTrigger="onConfirm"
            rules={[
              {
                required: true,
                message: t("sensechat-276") as string, // "请输入密码"
              },
              {
                required: true,
                min: 6,
                message: t("sensechat-277") as string, // "密码至少6位"
              },
              {
                validator: (_: any, value: string) => {
                  if (!validatePassword(value)) {
                    return Promise.reject(
                      new Error(t("sensechat-278") as string),
                    ); // 密码必须同时包含数字及大小写字母
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
          >
            <Input.Password
              placeholder={t("sensechat-82") as string} // 请输入密码
              autoComplete="off"
              className="large-input"
              iconRender={PasswordEye}
              prefix={<UserPasswordIcon style={INPUT_ICON_STYLE} />}
              maxLength={100}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        )}
        <p className={styles["login-register-tip"]}>
          未注册手机号验证通过后将自动注册
        </p>

        <div className={styles["agree-text-container"]}>
          <Form.Item
            name="agreement"
            className={styles["checkbox-item"]}
            validateTrigger="onConfirm"
            valuePropName="checked"
          >
            <Checkbox className={styles["login-checkbox"]}>
              <span className={styles["agree-text"]}>
                已阅读并同意
                <span
                  className={styles["new-login-underline"]}
                  onClick={e => handleShowAgreement(e, AgreeType.userPrivacy)}
                >
                  《用户协议》
                </span>
                和
                <span
                  className={styles["new-login-underline"]}
                  onClick={e => handleShowAgreement(e, AgreeType.infoCollect)}
                >
                  《信息政策》
                </span>
              </span>
            </Checkbox>
          </Form.Item>
          {authVM.loginType === ELoginType.password && (
            <span
              className={styles["forget-pswd"]}
              onClick={onGotoResetPassword}
            >
              忘记密码
            </span>
          )}
        </div>
        <div>
          <SJButton
            block
            size="large"
            type="primary"
            onClick={handleLogin}
            className={cls(
              styles["new-login-button"],
              styles["new-login-button-last"],
            )}
          >
            登录
          </SJButton>
        </div>
      </Form>
      <PolicyDialog
        visible={showProtocol}
        onCancel={() => setShowProtocol(false)}
        onOk={handleAgreeProtocol}
      />
      {message.contextHolder}
    </>
  );
};

export default observer(LoginDialog);
